import {
  PaneContents,
  ViewMode,
  ViewModeCapabilityCoordinator,
  viewModeEquals,
} from '@mobile-mapping/web3d';
import { PaneElementProps } from '@trimble/pane-layout';
import { Accessor, createEffect, createMemo, on } from 'solid-js';

export interface PaneViewModeBridgeParams {
  readonly layoutConfig: PaneElementProps<
    PaneContents,
    ViewMode
  >['layoutConfig'];
  readonly viewModeCapCoord: Accessor<
    ViewModeCapabilityCoordinator | undefined
  >;
}

export interface PaneViewModeBridgeResult {
  readonly viewModeFromLayout: Accessor<ViewMode>;
}

/** @intent Two-way sync of layout cell contentConfig with ViewModeCapabilityCoordinator.viewMode. */
export function usePaneViewModeBridge(
  params: PaneViewModeBridgeParams
): PaneViewModeBridgeResult {
  const viewModeFromLayout = createMemo(
    (prev: ViewMode | undefined): ViewMode => {
      const activeLayout = params.layoutConfig.layouts.layouts.find(
        (l) => l.label === params.layoutConfig.layouts.activeLayoutLabel
      );
      if (activeLayout === undefined) {
        throw new Error('Active layout must be selected');
      }

      const activeRow = activeLayout.paneRows[params.layoutConfig.rowIdx];
      const activeCell = activeRow.cells[params.layoutConfig.cellIdx];

      const next: ViewMode =
        activeCell.contentConfig.type !== 'Image'
          ? {
              type: activeCell.contentConfig.type,
            }
          : {
              type: activeCell.contentConfig.type,
              cameraIndex: activeCell.contentConfig.cameraIndex,
            };

      if (!prev) return next;
      return viewModeEquals(prev, next) ? prev : next;
    }
  );

  createEffect(
    on(
      () => params.layoutConfig.layouts.activeLayoutLabel,
      () => {
        const vm = viewModeFromLayout();
        const coord = params.viewModeCapCoord();
        if (coord) {
          coord.setViewMode(vm);
        }
      },
      { defer: true }
    )
  );

  createEffect(
    on(
      () => params.viewModeCapCoord()?.viewMode(),
      (vm) => {
        if (!vm) return;

        const activeLabel = params.layoutConfig.layouts.activeLayoutLabel;

        params.layoutConfig.setLayouts(
          'layouts',
          (layouts) => layouts.label === activeLabel,
          'paneRows',
          params.layoutConfig.rowIdx,
          'cells',
          params.layoutConfig.cellIdx,
          'contentConfig',
          vm
        );
      }
    )
  );

  return { viewModeFromLayout };
}
